import axiosInstance from '@/api/axiosInstance'
import { generateCardHTML } from '@/config/cardGenerator'
import { AuthContext } from '@/contexts/auth-context'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { useContext, useEffect, useState } from 'react'
import { RiGiftLine, RiRefreshLine, RiStarLine, RiAddLine } from 'react-icons/ri'
import { useNavigate } from 'react-router-dom'
import AddToWalletPopup from '../../components/user-component/AddToWalletPopup'

function UserLoyaltyCards() {
  const { auth } = useContext(AuthContext)
  const [showWalletPopup, setShowWalletPopup] = useState(false)
  const navigate = useNavigate()

  const { data, isLoading, error } = useQuery({
    queryKey: ['userLoyaltyCards', auth.user._id],
    queryFn: async () => {
      const response = await axiosInstance.get('web_api/customer/cardList', {
        params: { userId: auth.user._id },
      })
      return response.data
    },
  })

  const [scaledWidth, setScaledWidth] = useState(300) // default fallback

  const baseCardWidth = 700

  const calculateScale = () => {
    const width = window.innerWidth
    if (width >= 1024) return 0.6
    if (width >= 768) return 0.5
    return 0.46
  }

  const updateScaledWidth = () => {
    const scale = calculateScale()
    setScaledWidth(baseCardWidth * scale)
  }

  useEffect(() => {
    updateScaledWidth()
    window.addEventListener('resize', updateScaledWidth)
    return () => window.removeEventListener('resize', updateScaledWidth)
  }, [])

  if (isLoading) {
    return (
      <div className="container mx-auto p-4">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">My Loyalty Cards</h3>
        <div className="flex justify-center items-center py-20">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    )
  }

  // Handle all cases where we don't have valid card data
  if (error?.response?.status === 404 || !data?.data?.cards || data.data.cards.length === 0) {
    return (
      <div className="container mx-auto p-4">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">My Loyalty Cards</h3>
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <img
            src="/customer-assets/no-data.png"
            alt="No Cards"
            className="w-40 h-40 mx-auto  opacity-75"
          />
          <p className="text-gray-500 text-lg">You don't have any loyalty cards yet.</p>
        </div>
      </div>
    )
  }

  // Handle unexpected errors
  if (error) {
    return (
      <div className="container mx-auto p-4">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">My Loyalty Cards</h3>
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <p className="text-red-500">Something went wrong while loading your cards.</p>
          <p className="text-gray-500 text-sm mt-2">Please try again later.</p>
        </div>
      </div>
    )
  }

  // Show cards if we have data
  return (
    <div className="container mx-auto p-4">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">My Loyalty Cards</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data.data.cards
          .filter((cardData) => cardData.isCustomerActive) // only active customers
          .map((cardData, index) => (
            <div
              key={index}
              className="flex flex-col items-center w-full justify-center sm:border border-dashed rounded-xl sm:rounded-2xl sm:bg-gradient-to-br from-pink-200 via-sky-100 to-blue-200 sm:shadow py-2 pt-0 sm:pt-4 sm:pb-2 "
            >
              {/* Card Container */}
              <div className="relative w-[300px] md:w-[490px] lg:w-[490px] h-[160px] md:h-[180px] lg:h-[210px] flex items-center justify-center">
                <div
                  className="transform scale-[0.46] md:scale-[0.5] lg:scale-[0.6]"
                  style={{ width: `${baseCardWidth}px`, height: '350px' }}
                  dangerouslySetInnerHTML={{ __html: generateCardHTML(cardData.card) }}
                />
              </div>

              {/* Bonus Points Display */}
              <div
                className="mx-auto px-2 flex flex-wrap items-center justify-center md:justify-between gap-2 mt-2"
                style={{ width: `${scaledWidth}px` }}
              >
                {/* Points */}
                <motion.div
                  className="flex items-center gap-1 py-1 bg-gradient-to-r from-yellow-50 to-orange-50 px-2 rounded-xl shadow-sm border border-yellow-100"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <RiStarLine className="w-5 h-5 text-yellow-400" />
                  <span className="text-xs text-yellow-600 font-medium">Points</span>
                  <span className="text-sm font-bold text-yellow-700">{cardData.rewardPoints}</span>
                </motion.div>

                {/* Rewards */}
                <motion.div
                  className="flex items-center gap-1 py-1 bg-gradient-to-r cursor-pointer from-pink-50 to-rose-50 px-2 rounded-xl shadow-sm border border-pink-100"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  onClick={() =>
                    cardData?.businessId &&
                    navigate(`/user/rewards/${cardData.businessId}`, {
                      state: { businessDetails: cardData.businessDetails },
                    })
                  }
                >
                  <RiGiftLine className="w-5 h-5 text-pink-400" />
                  <span className="text-xs text-pink-600 font-medium">Rewards</span>
                  <span className="text-sm font-bold text-pink-700">
                    {cardData.totalRewardsCount}
                  </span>
                </motion.div>

                {/* Add to Wallet */}
                <motion.div
                  className="relative group"
                  initial={{ opacity: 0, x: 0 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.15 }}
                  onClick={() => setShowWalletPopup(true)}
                >
                  <div className="flex items-center cursor-pointer gap-1 py-1 bg-gradient-to-r from-purple-50 to-violet-50 px-2 rounded-xl shadow-sm border border-purple-100">
                    <RiAddLine className="w-5 h-5 text-emerald-500" />
                    <button className="text-sm font-medium cursor-pointer text-emerald-500 hover:text-emerald-600 transition-all">
                      Add
                    </button>
                  </div>

                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                    Add Card to Wallet
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
                  </div>
                </motion.div>

                {/* Switch */}
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  onClick={() =>
                    cardData?.businessId &&
                    navigate(cardData.businessId, {
                      state: { businessDetails: cardData.businessDetails },
                    })
                  }
                >
                  <div className="flex items-center cursor-pointer gap-1 py-1 bg-gradient-to-r from-purple-50 to-violet-50 px-2 rounded-xl shadow-sm border border-purple-100">
                    <RiRefreshLine className="w-4 h-4 text-purple-400 cursor-pointer" />
                    <button className="text-sm font-medium cursor-pointer text-purple-600 hover:text-purple-700 transition-all">
                      Switch
                    </button>
                  </div>
                </motion.div>
              </div>
              <AddToWalletPopup
                isOpen={showWalletPopup}
                onClose={() => setShowWalletPopup(false)}
                cardData={cardData}
              />
            </div>
          ))}
      </div>
    </div>
  )
}

export default UserLoyaltyCards
